import React from 'react';
const { useState, useEffect } = React;
import { inputSt, labelSt, mutedSt, warnSt } from '../diagnostic/shared.jsx';

// Choose an existing person (the shared identity card) or create one without
// leaving the screen. Used by Diagnostic and Acompañamiento; each passes its
// own server calls, and the server decides what each one may see (basic data:
// name, email, time zone and contact).
// - Same email: the server refuses a new card and returns the existing ones;
//   here they are offered to reuse, and "Es otra persona" creates another one.
// - From a contact: the card linked to it is offered first; then the cards
//   with the same email (reusing one links it to the contact); only then a new
//   card, created linked to the contact.
// Nothing is merged or linked without a click.
//
// api: { search(q) → [card], create(person, { confirmNew, stakeholderId }) → card,
//        contacts?(q) → [contact], fromContact?(id) → { contact, linked, suggestions },
//        linkContact?(personId, stakeholderId) → card }

const smallBtn = { fontSize: 11, padding: '2px 8px' };
const Card = ({ p, action, onClick }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'center', fontSize: 13, padding: '4px 0', flexWrap: 'wrap' }}>
    <span><b>{p.name}</b>{p.email ? <span style={mutedSt}> · {p.email}</span> : <span style={mutedSt}> · sin email</span>}</span>
    <button type="button" className="action-btn" style={smallBtn} onClick={onClick}>{action}</button>
  </div>
);

function CreateForm({ initial, contact, api, onPick, onCancel }) {
  const [v, setV] = useState({ name: initial?.name || '', email: initial?.email || '' });
  const [matches, setMatches] = useState(null); // { reason, list }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const create = async (confirmNew = false) => {
    setBusy(true); setError('');
    try { onPick(await api.create({ name: v.name.trim(), email: v.email.trim() }, { confirmNew, stakeholderId: contact?.id })); }
    catch (e) {
      if (e.code === 'person_exists') setMatches({ reason: e.data?.reason, list: e.data?.matches || [] });
      else setError(e.message);
    }
    setBusy(false);
  };
  const use = async (p) => {
    // Reusing a card for a contact links it to that contact (explicitly, with history)
    if (contact && api.linkContact && p.stakeholderId !== contact.id) {
      try { onPick(await api.linkContact(p.id, contact.id)); } catch (e) { setError(e.message); }
    } else onPick(p);
  };
  return (
    <div style={{ display: 'grid', gap: 8, padding: 10, border: '1px solid var(--globant-border)', borderRadius: 8 }}>
      <b style={{ fontSize: 13 }}>{contact ? `Nueva ficha desde el contacto ${contact.name}` : 'Nueva persona'}</b>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <label style={{ flex: '1 1 180px' }}><span style={labelSt}>Nombre</span><input style={inputSt} value={v.name} onChange={e => { setV({ ...v, name: e.target.value }); setMatches(null); }} /></label>
        <label style={{ flex: '1 1 200px' }}><span style={labelSt}>Email</span><input style={inputSt} value={v.email} onChange={e => { setV({ ...v, email: e.target.value }); setMatches(null); }} /></label>
      </div>
      <div style={mutedSt}>La ficha tiene solo datos básicos y no necesita empresa: la empresa va en cada participación.</div>
      {matches && (
        <div style={{ ...warnSt, padding: 10, marginBottom: 0 }}>
          <div style={{ fontSize: 12, marginBottom: 4 }}>{matches.reason === 'contact_linked' ? 'Este contacto ya tiene una ficha. Úsala:' : 'Ya hay una ficha con ese email. ¿Es la misma persona?'}</div>
          {matches.list.map(p => <Card key={p.id} p={p} action="Usar esta ficha" onClick={() => use(p)} />)}
          {matches.reason !== 'contact_linked' && <button type="button" className="action-btn" style={{ ...smallBtn, marginTop: 6 }} disabled={busy} onClick={() => create(true)}>Es otra persona: crear otra ficha</button>}
        </div>
      )}
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</div>}
      <div style={{ display: 'flex', gap: 6 }}>
        <button type="button" className="action-btn btn-primary" disabled={busy || !v.name.trim() || !!matches} onClick={() => create(false)}>{busy ? 'Creando…' : 'Crear y elegir'}</button>
        <button type="button" className="action-btn" onClick={onCancel}>Cancelar</button>
      </div>
    </div>
  );
}

function FromContact({ api, onPick, onCancel }) {
  const [q, setQ] = useState('');
  const [list, setList] = useState(null);
  const [state, setState] = useState(null); // personFromContact result
  const [error, setError] = useState('');
  useEffect(() => {
    const t = setTimeout(() => api.contacts(q).then(setList).catch(e => setError(e.message)), 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const pick = async (c) => { setError(''); try { setState(await api.fromContact(c.id)); } catch (e) { setError(e.message); } };
  const link = async (p) => { try { onPick(await api.linkContact(p.id, state.contact.id)); } catch (e) { setError(e.message); } };
  if (state?.creating) return <CreateForm initial={state.contact} contact={state.contact} api={api} onPick={onPick} onCancel={() => setState({ ...state, creating: false })} />;
  return (
    <div style={{ display: 'grid', gap: 6, padding: 10, border: '1px solid var(--globant-border)', borderRadius: 8 }}>
      {!state ? (
        <>
          <input style={inputSt} value={q} placeholder="Busca un contacto de la cuenta" aria-label="Buscar contacto" onChange={e => setQ(e.target.value)} />
          {list && !list.length && <div style={mutedSt}>No hay contactos que coincidan.</div>}
          {list?.map(c => <Card key={c.id} p={{ ...c, name: `${c.name}${c.role ? ` (${c.role})` : ''}` }} action="Elegir" onClick={() => pick(c)} />)}
        </>
      ) : (
        <>
          <b style={{ fontSize: 13 }}>Contacto: {state.contact.name}</b>
          {state.linked ? (
            <><div style={mutedSt}>Este contacto ya tiene ficha:</div><Card p={state.linked} action="Usar esta ficha" onClick={() => onPick(state.linked)} /></>
          ) : (
            <>
              {state.suggestions.length > 0 && <>
                <div style={mutedSt}>Hay fichas con el mismo email. Si es la misma persona, usa la suya: queda vinculada a este contacto.</div>
                {state.suggestions.map(p => <Card key={p.id} p={p} action="Usar y vincular al contacto" onClick={() => link(p)} />)}
              </>}
              <button type="button" className="action-btn" style={{ ...smallBtn, justifySelf: 'start' }} onClick={() => setState({ ...state, creating: true })}>Crear ficha nueva desde el contacto</button>
            </>
          )}
          <button type="button" className="action-btn" style={{ ...smallBtn, justifySelf: 'start' }} onClick={() => setState(null)}>Otro contacto</button>
        </>
      )}
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</div>}
      <button type="button" className="action-btn" style={{ ...smallBtn, justifySelf: 'start' }} onClick={onCancel}>Cancelar</button>
    </div>
  );
}

export default function PersonPicker({ api, onPick, exclude = [] }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState(null);
  const [mode, setMode] = useState('search'); // search | create | contact
  const [error, setError] = useState('');
  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return undefined; }
    const t = setTimeout(() => api.search(q).then(r => { setResults(r); setError(''); }).catch(e => setError(e.message)), 250);
    return () => clearTimeout(t);
  }, [q]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (results || []).filter(p => !exclude.includes(p.id));
  if (mode === 'create') return <CreateForm initial={{ name: q.includes('@') ? '' : q, email: q.includes('@') ? q : '' }} api={api} onPick={onPick} onCancel={() => setMode('search')} />;
  if (mode === 'contact') return <FromContact api={api} onPick={onPick} onCancel={() => setMode('search')} />;
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <input style={inputSt} value={q} placeholder="Busca una persona por nombre o email" aria-label="Buscar persona" onChange={e => setQ(e.target.value)} />
      {error && <div role="alert" style={{ color: '#9A6212', fontSize: 12 }}>{error}</div>}
      {results && !shown.length && <div style={mutedSt}>No hay personas que coincidan{results.length ? ' (las que coinciden ya están aquí)' : ''}.</div>}
      {shown.map(p => <Card key={p.id} p={p} action="Elegir" onClick={() => onPick(p)} />)}
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button type="button" className="action-btn" style={smallBtn} onClick={() => setMode('create')}>+ Crear persona nueva</button>
        {api.contacts && <button type="button" className="action-btn" style={smallBtn} onClick={() => setMode('contact')}>Desde un contacto de la cuenta</button>}
      </div>
    </div>
  );
}
